import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { LucideIcon } from 'lucide-react';

const variants = {
  primary: 'bg-legal-green-500 text-white hover:bg-legal-green-400 focus-visible:ring-legal-green-600',
  secondary: 'bg-surface-elevated text-text-primary border border-border-primary hover:bg-surface-inset focus-visible:ring-legal-green-600',
  outline: 'bg-transparent text-text-primary border border-border-primary hover:bg-surface-inset focus-visible:ring-legal-green-600',
  ghost: 'bg-transparent text-text-secondary hover:bg-surface-inset hover:text-text-primary focus-visible:ring-legal-green-600',
  danger: 'bg-crimson-600 text-white hover:bg-crimson-700 focus-visible:ring-crimson-500',
  gold: 'bg-legal-green-500 text-white hover:bg-legal-green-400 focus-visible:ring-legal-green-600 font-semibold',
  // Chamber CTA — sharp brass outline that fills on hover (mirrors the home
  // "+ NEW MATTER" button in ChambersSearchRow). Sharp corners override the
  // base rounded-lg (see radius handling below).
  brass: 'border border-brass text-brass-ink hover:bg-brass hover:text-chamber-ink focus-visible:ring-brass font-semibold',
};

/** Chamber variants render with sharp corners; everything else keeps rounded-lg. */
const SHARP_VARIANTS = new Set<keyof typeof variants>(['brass']);

const sizes = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2',
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, icon: Icon, iconRight: IconRight, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-colors duration-200 cursor-pointer',
          SHARP_VARIANTS.has(variant) ? 'rounded-none' : 'rounded-lg',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : Icon ? (
          <Icon className="h-4 w-4" />
        ) : null}
        {children}
        {IconRight && !loading && <IconRight className="h-4 w-4" />}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
